import type { Shoot } from '../types/portal'
import { isoOf, shootDates } from './projectShoots'
import { entryDates, type CalendarEntry } from './calendarEntries'
import type { MirroredEvent } from './calendarMirror'

/**
 * A run of days, as one list.
 *
 * Tom and Zeena, 23 Sep 2026: somewhere that says what is coming up before
 * the week starts, rather than reading it off a month grid. This is that, and
 * deliberately only that. An earlier draft also carried a "before the week
 * starts" section and a to-do list, and three things on one screen was worse
 * than one. Needs Attention already says what is WRONG; this says what is ON.
 *
 * It invents nothing and asks no model anything: every line is composed from
 * rows Portal OS already holds, so the screen is instant, costs nothing, and
 * cannot announce a shoot that does not exist.
 *
 * Same three layers as the Calendar tab, and the same rule about them:
 * anything Portal OS put into Google comes back in the mirror, so a shoot or
 * a diary entry the app owns is filtered out of the Google layer before it
 * reaches here. Otherwise every shoot appears twice — the duplication the
 * fill-the-gaps rule exists to prevent, reintroduced visually.
 *
 * **24 Sep 2026: this also composes the phone Calendar.** `composeWeek` takes
 * whatever list of dates it is given, so a month is just a longer window —
 * which is why `sourceId` exists: the week only reads, but the calendar's
 * agenda has to be able to open the thing you tapped.
 */

export const WEEK_DAYS = 7

export type WeekItemKind = 'shoot' | 'entry' | 'google'

export interface WeekItem {
  key: string
  kind: WeekItemKind
  /** The whole line, already written. */
  text: string
  /** "08:00", or '' for an all-day thing. A sort key, not for display. */
  time: string
  /** A standing commitment — the radio slot, travel. Quieter, and sorted last. */
  quiet: boolean
  /**
   * The lead story on its day — drawn larger, the way a paper sizes its
   * front page. See `isLead` below for what qualifies and why.
   */
  lead: boolean
  /** Set on a shoot, so a caller can open its project. */
  projectId?: string
  /**
   * The id of the row this line was written from — a shoot id, an entry id,
   * or a Google event id, depending on `kind`. The Week screen ignores it;
   * the phone Calendar uses it to find the object to edit, since the line
   * itself is only text by the time it gets here.
   */
  sourceId?: string
}

export interface WeekDay {
  /** The first date this row covers. */
  iso: string
  /** "Wed 23", or "Sat 26 – Tue 29" for a run of clear days. */
  label: string
  isToday: boolean
  items: WeekItem[]
}

export interface WeekSource {
  shoots: { shoot: Shoot; projectId: string; client: string; project: string }[]
  entries: CalendarEntry[]
  events: MirroredEvent[]
}

/**
 * WHAT COUNTS AS A LEAD. 16 Oct 2026.
 *
 * Portal OS has no colour, so for a year it also had no way to say that one
 * line on a screen mattered more than another. On the Week Ahead that showed
 * plainly: *DEADLINE — Al Futtaim Electric Mobility Proposal* was set in the
 * same grey, at the same size, on the same row height as the word "Clear".
 * The one line with a consequence attached looked exactly like nothing
 * happening.
 *
 * Rather than introduce an accent colour, emphasis comes from scale — which
 * the typeface carries well, as the setup flow's 6.5rem titles prove. Two
 * things qualify:
 *
 *   • **A shoot.** A shoot day is the one thing in the week that cannot be
 *     moved without moving other people, so it is the week's spine.
 *   • **A deadline.** Anything whose own title says deadline, due or
 *     delivery — the words Tom actually types into the diary.
 *
 * Matched on the TITLE, never on the composed line, so a meeting held at a
 * place called The Delivery Room is not promoted by its location. Kept
 * deliberately short: a rule that promotes half the week promotes nothing.
 */
const LEAD_WORDS = /\b(deadline|due|delivery|delivering)\b/i

export function isLead(title: string | undefined | null): boolean {
  return Boolean(title && LEAD_WORDS.test(title))
}

/** The seven ISO dates from today. */
export function weekWindow(today: Date = new Date()): string[] {
  const days: string[] = []
  for (let offset = 0; offset < WEEK_DAYS; offset += 1) {
    const date = new Date(today)
    date.setHours(0, 0, 0, 0)
    date.setDate(date.getDate() + offset)
    days.push(isoOf(date))
  }
  return days
}

/** Every ISO date in a month — the window the phone Calendar composes. */
export function monthWindow(month: Date): string[] {
  const year = month.getFullYear()
  const index = month.getMonth()
  const length = new Date(year, index + 1, 0).getDate()
  return Array.from({ length }, (_, offset) => isoOf(new Date(year, index, offset + 1)))
}

/** "Wed 23" — split rather than Date-parsed, so no timezone shift. */
export function dayLabel(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return iso
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
  })
}

/** "Wed 23 – Tue 29 September", the range the page is showing. */
export function weekLabel(days: string[]): string {
  if (days.length === 0) return ''
  const first = days[0].split('-').map(Number)
  const last = days[days.length - 1].split('-').map(Number)
  const start = new Date(first[0], first[1] - 1, first[2])
  const end = new Date(last[0], last[1] - 1, last[2])
  /* The month is printed once when both ends share it — the point of the line
     is the span, and a repeated month is just more to read. */
  const sameMonth = start.getMonth() === end.getMonth()
  const left = start.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    ...(sameMonth ? {} : { month: 'short' }),
  })
  const right = end.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' })
  return `${left} – ${right}`
}

/** One line, commas between whatever is actually there. */
function sentence(parts: (string | undefined)[]): string {
  return parts
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(', ')
}

/**
 * Ordering within a day: the lead first, the standing commitments last, and
 * everything else by the clock with all-day things at the top.
 *
 * Quiet last rather than hidden, because you need to see the radio slot when
 * you are deciding where to put a shoot — it just should not read first.
 */
function byTime(a: WeekItem, b: WeekItem): number {
  if (a.quiet !== b.quiet) return a.quiet ? 1 : -1
  if (a.lead !== b.lead) return a.lead ? -1 : 1
  return (a.time || '00:00').localeCompare(b.time || '00:00')
}

/** Every day in the window, with what is on it. */
export function composeWeek(
  days: string[],
  source: WeekSource,
  today: string = isoOf(new Date()),
): WeekDay[] {
  const byDay = new Map<string, WeekItem[]>()
  for (const iso of days) byDay.set(iso, [])

  function add(iso: string, item: WeekItem) {
    const list = byDay.get(iso)
    if (list) list.push(item)
  }

  for (const { shoot, projectId, client, project } of source.shoots) {
    const who = [client, project, shoot.title].filter(Boolean).join('  ·  ')
    const text = sentence([`Shoot — ${who}`, shoot.time, shoot.location])
    for (const iso of shootDates(shoot)) {
      add(iso, {
        key: `shoot-${shoot.id}-${iso}`,
        kind: 'shoot',
        text,
        time: shoot.time,
        quiet: false,
        /* A shoot is always the lead — see isLead. */
        lead: true,
        projectId,
        sourceId: shoot.id,
      })
    }
  }

  for (const entry of source.entries) {
    /* A 'duplicate' is an entry the worker deliberately did not push, because
       the same meeting was already on the company calendar by hand. That hand
       -made copy IS in the mirror and will be drawn, so drawing this one too
       puts one meeting on the day twice, worded two ways — which on a plain
       list reads as two meetings rather than as a clash to resolve. Needs
       Attention is where a person is told the pair exists; this screen just
       shows the week, so it shows the copy that is actually in the diary. */
    if (entry.syncState === 'duplicate') continue

    const text = sentence([entry.title, entry.startTime, entry.location])
    for (const iso of entryDates(entry)) {
      add(iso, {
        key: `entry-${entry.id}-${iso}`,
        kind: 'entry',
        text,
        time: entry.startTime,
        quiet: false,
        lead: isLead(entry.title),
        sourceId: entry.id,
      })
    }
  }

  for (const event of source.events) {
    const text = sentence([event.summary, event.startTime, event.location])
    /* Walked day by day so a run shows on each of its days, the same way the
       month grid draws it. Guarded in case a mirrored row has a silly range. */
    const cursor = new Date(`${event.startDate}T00:00:00`)
    const end = new Date(`${event.endDate || event.startDate}T00:00:00`)
    let guard = 0
    while (cursor <= end && guard < 100) {
      const iso = isoOf(cursor)
      add(iso, {
        key: `google-${event.eventId}-${iso}`,
        kind: 'google',
        text,
        time: event.startTime,
        quiet: event.notProduction,
        /* A standing commitment is never a lead, whatever it is called. */
        lead: !event.notProduction && isLead(event.summary),
        sourceId: event.eventId,
      })
      cursor.setDate(cursor.getDate() + 1)
      guard += 1
    }
  }

  return days.map((iso) => ({
    iso,
    label: dayLabel(iso),
    isToday: iso === today,
    items: (byDay.get(iso) ?? []).sort(byTime),
  }))
}

/**
 * Runs of empty days become one row.
 *
 * Four rows each saying "Clear" is four rows of nothing; "Sat 26 – Tue 29,
 * clear" is the same fact at a glance, and makes the days that DO have
 * something on them stand out — which is the whole point of the screen.
 *
 * The phone Calendar deliberately does NOT use this: on that screen a free
 * day is the thing you are hunting for, and collapsing the free days hides
 * the answer and the + that acts on it.
 */
export function collapseClearRuns(week: WeekDay[]): WeekDay[] {
  const out: WeekDay[] = []
  let run: WeekDay[] = []

  function flush() {
    if (run.length === 0) return
    const first = run[0]
    const last = run[run.length - 1]
    out.push({
      ...first,
      label: run.length === 1 ? first.label : `${first.label} – ${last.label}`,
      isToday: run.some((day) => day.isToday),
    })
    run = []
  }

  for (const day of week) {
    if (day.items.length === 0) {
      run.push(day)
      continue
    }
    flush()
    out.push(day)
  }
  flush()
  return out
}
